import { useLayoutEffect } from 'react';
import type {
  ScrollDirection,
  UseScrollIntentParams,
} from '../../types/scroll/scroll-intent.types';
import { getScrollLayoutRevision } from '../../utils/scroll/scroll-layout.util';

/**
 * Observes intentional vertical input without interpreting data or moving the view.
 * Programmatic scrolls alone never trigger the callback. Input at an already reached
 * edge also works for content shorter than the viewport.
 * @param params - Viewport, lifetime and input consumer.
 */
export const useScrollIntent = ({
  containerRef,
  layoutBoundaryRef,
  enabled,
  resetKey,
  positionRevision,
  onIntent,
}: UseScrollIntentParams): void => {
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;
    let previousTop = container.scrollTop;
    let input: ScrollDirection | 'either' | null = null;
    let touchY: number | null = null;
    let pointerX: number | null = null;
    let pointerY: number | null = null;
    const readLayout = () =>
      getScrollLayoutRevision(container, layoutBoundaryRef?.current);
    let inputLayout = readLayout();
    const recordInput = (direction: ScrollDirection | 'either' | null) => {
      input = direction;
      inputLayout = readLayout();
    };
    const consume = (direction: ScrollDirection, event: Event) => {
      if (onIntent(direction, event)) input = null;
    };
    const atEdge = (direction: ScrollDirection) =>
      direction === 'up'
        ? container.scrollTop <= 0
        : container.scrollHeight -
            container.scrollTop -
            container.clientHeight <=
          0;
    const handleScroll = (event: Event) => {
      const top = container.scrollTop;
      const direction =
        top < previousTop ? 'up' : top > previousTop ? 'down' : null;
      previousTop = top;
      // An unfinished gesture does not make a later layout clamp user movement.
      // Fresh wheel/key/touch/drag input records the new geometry and still wins.
      if (input && inputLayout !== readLayout()) {
        input = null;
        return;
      }
      if (direction && (input === direction || input === 'either')) {
        consume(direction, event);
      } else if (direction) {
        input = null;
      }
    };
    const handleWheel = (event: WheelEvent) => {
      const direction =
        event.deltaY < 0 ? 'up' : event.deltaY > 0 ? 'down' : null;
      recordInput(direction);
      if (direction && atEdge(direction)) consume(direction, event);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      input = null;
      if (event.target !== container) return;
      let direction: ScrollDirection | null = null;
      if (['ArrowUp', 'PageUp', 'Home'].includes(event.key)) {
        direction = 'up';
      }
      if (['ArrowDown', 'PageDown', 'End'].includes(event.key)) {
        direction = 'down';
      }
      recordInput(direction);
      if (direction && atEdge(direction)) consume(direction, event);
    };
    const handleTouchStart = (event: TouchEvent) => {
      touchY = event.touches.item(0)?.clientY ?? null;
      input = null;
    };
    const handleTouchMove = (event: TouchEvent) => {
      const y = event.touches.item(0)?.clientY;
      const direction =
        y !== undefined && touchY !== null && y !== touchY
          ? y > touchY
            ? 'up'
            : 'down'
          : null;
      recordInput(direction);
      if (direction && atEdge(direction)) consume(direction, event);
      touchY = y ?? null;
    };
    const handlePointerDown = (event: PointerEvent) => {
      pointerX = event.clientX;
      pointerY = event.clientY;
      recordInput('either');
    };
    const handlePointerMove = (event: PointerEvent) => {
      if (
        event.buttons === 1 &&
        event.pointerType !== 'touch' &&
        (event.clientX !== pointerX || event.clientY !== pointerY)
      ) {
        recordInput('either');
      }
      pointerX = event.clientX;
      pointerY = event.clientY;
    };
    const clearInput = () => {
      input = null;
    };
    container.addEventListener('scroll', handleScroll);
    container.addEventListener('wheel', handleWheel, { passive: true });
    container.addEventListener('keydown', handleKeyDown);
    container.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    container.addEventListener('pointerdown', handlePointerDown);
    container.addEventListener('pointermove', handlePointerMove);
    container.addEventListener('pointerup', clearInput);
    container.addEventListener('pointercancel', clearInput);
    container.addEventListener('scrollend', clearInput);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('keydown', handleKeyDown);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('pointerdown', handlePointerDown);
      container.removeEventListener('pointermove', handlePointerMove);
      container.removeEventListener('pointerup', clearInput);
      container.removeEventListener('pointercancel', clearInput);
      container.removeEventListener('scrollend', clearInput);
    };
  }, [
    containerRef,
    layoutBoundaryRef,
    enabled,
    resetKey,
    positionRevision,
    onIntent,
  ]);
};
